import React, { useEffect, useMemo, useState } from "react";
import { getContent, setContent, listAll } from "../../services/firestore";
import { useApp } from "../../contexts/AppContext";
import { uploadImageToR2 } from "../../services/r2";
import {
  Save, Upload, Plus, Trash2, ChevronUp, ChevronDown, Music, Disc3,
  Search, Tag, Share2, Image as ImageIcon, Newspaper, Home, Grid3x3,
  User, Gift, Info, Archive, FileText, AlertCircle,
} from "lucide-react";

// ─── Constants ──────────────────────────────────────────────────────────
const FIELDS = [
  { section: "brand", title: "Brend / Loqo", items: [
    { key: "name", label: "Sayt adı (loqo mətni)", multiline: false },
    { key: "tagline", label: "Şüar (loqo altında, istəyə bağlı)", multiline: false },
  ]},
  { section: "footer", title: "Alt hissə (Footer)", items: [
    { key: "about", label: "Haqqımızda", multiline: true },
    { key: "address", label: "Ünvan", multiline: true },
    { key: "copyright", label: "Müəlliflik hüququ", multiline: false },
    { key: "tagline", label: "Şüar", multiline: false },
  ]},
];

const HERO_FIELDS = [
  { key: "eyebrow", label: "Üst yazı / Şüar", multiline: false },
  { key: "title", label: "Başlıq", multiline: true },
  { key: "desc", label: "Təsvir", multiline: true },
];

const LANGS = ["az", "en", "ru"];
const ALIGNS = [
  { v: "left", l: "Sol" },
  { v: "center", l: "Mərkəz" },
  { v: "right", l: "Sağ" },
];
const EDITORIAL_ALIGNS = [
  { v: "left", l: "Şəkil solda, mətn sağda" },
  { v: "right", l: "Şəkil sağda, mətn solda" },
];
const EDITORIAL_ICONS = [
  { v: "vinyl", l: "Vinyl (disk)" },
  { v: "sparkles", l: "Sparkles (hədiyyə)" },
  { v: "headphones", l: "Headphones (sənətçi)" },
  { v: "none", l: "İkon yoxdur" },
];

// Page-level CMS schema
const PAGE_SCHEMA = [
  {
    key: "home", title: "Ana — bölmə başlıqları",
    desc: "Ana səhifədə bannerlərdən sonrakı bölmələrin üst yazıları və başlıqları.",
    fields: [
      { key: "new_arrivals_eyebrow", label: "“Yeni gələnlər” üst yazısı", multiline: false,
        defaults: { az: "QURADILMIŞ", en: "CURATED", ru: "ВЫБРАНО" } },
      { key: "new_arrivals_title", label: "“Yeni gələnlər” başlığı", multiline: false,
        defaults: { az: "Yeni gələnlər", en: "New arrivals", ru: "Новинки" } },
      { key: "best_sellers_eyebrow", label: "“Ən çox satılanlar” üst yazısı", multiline: false,
        defaults: { az: "ÖN PLANDA", en: "FEATURED", ru: "ИЗБРАННОЕ" } },
      { key: "best_sellers_title", label: "“Ən çox satılanlar” başlığı", multiline: false,
        defaults: { az: "Ən çox satılanlar", en: "Best sellers", ru: "Бестселлеры" } },
      { key: "featured_artist_eyebrow", label: "“Seçilmiş sənətçi” üst yazısı (qırmızı)", multiline: false,
        defaults: { az: "SEÇİLMİŞ SƏNƏTÇİ", en: "FEATURED ARTIST", ru: "АРТИСТ МЕСЯЦА" } },
      { key: "journal_eyebrow", label: "“Oxumalı / Jurnal” üst yazısı (qırmızı)", multiline: false,
        defaults: { az: "JURNAL · YENİLİKLƏR", en: "JOURNAL · UPDATES", ru: "ЖУРНАЛ · НОВОСТИ" } },
      { key: "journal_title", label: "Jurnal bölməsi başlığı", multiline: false,
        defaults: { az: "Oxumalı.", en: "Read.", ru: "Читать." } },
      { key: "journal_lede", label: "Jurnal bölməsi qısa təsviri", multiline: true,
        defaults: {
          az: "Vinil mədəniyyəti, audiofil texnika və yerli səhnədən hekayələr.",
          en: "Stories from vinyl culture, audiophile gear and the local scene.",
          ru: "Истории винил-культуры, аудиофильной техники и местной сцены.",
        } },
    ],
    images: [],
  },
  {
    key: "home_masthead", title: "Ana — yuxarı zolaq (masthead)",
    desc: "Bannerdən sonra gələn nazik üfüqi zolaqdakı bütün yazılar: VOL. xx, tarix, sağa axan mətn (marquee) və “BÜTÜN KATALOQ” linki.",
    fields: [
      { key: "masthead_volume", label: "Sol tərəfdəki nömrə (məs: VOL. 47)", multiline: false,
        defaults: { az: "", en: "", ru: "" } },
      { key: "masthead_date", label: "Tarix aralığı (məs: FEB 10 — MAR 03)", multiline: false,
        defaults: { az: "", en: "", ru: "" } },
      { key: "masthead_label", label: "Sağ tərəfdəki etiket (məs: ANALOG WEEKLY)", multiline: false,
        defaults: { az: "", en: "", ru: "" } },
      { key: "masthead_marquee", label: "Sağa axan mətn — qaranlıq « · » ilə ayrılmış parçalar (məs: ANALOQ RUH · BAKIDA PRESLƏNİB · 180G TƏMİZ VİNİL · ...)", multiline: true,
        defaults: { az: "", en: "", ru: "" } },
      { key: "masthead_cta", label: "Sağ kənardakı düymə yazısı (məs: BÜTÜN KATALOQ)", multiline: false,
        defaults: { az: "", en: "", ru: "" } },
    ],
    images: [],
  },
  {
    key: "catalog", title: "Kataloq səhifəsi",
    desc: "Kataloq / Kateqoriyalar səhifəsinin başlıqları.",
    fields: [
      { key: "eyebrow", label: "Üst yazı", multiline: false,
        defaults: { az: "ARXİV", en: "ARCHIVE", ru: "АРХИВ" } },
      { key: "title", label: "Səhifə başlığı", multiline: false,
        defaults: { az: "Kataloq", en: "Catalog", ru: "Каталог" } },
    ],
    images: [],
  },
  {
    key: "artists", title: "Sənətçilər səhifəsi",
    desc: "Sənətçilər siyahısı səhifəsinin mətnləri.",
    fields: [
      { key: "eyebrow", label: "Üst yazı", multiline: false,
        defaults: { az: "ROSTER", en: "ROSTER", ru: "СОСТАВ" } },
      { key: "title", label: "Səhifə başlığı", multiline: false,
        defaults: { az: "Sənətçilər", en: "Artists", ru: "Артисты" } },
    ],
    images: [],
  },
  {
    key: "gift_card", title: "Hədiyyə Kartı səhifəsi",
    desc: "Hədiyyə kartı səhifəsinin başlığı və izahı.",
    fields: [
      { key: "eyebrow", label: "Üst yazı", multiline: false,
        defaults: { az: "VINYL BAKU · HƏDİYYƏ", en: "VINYL BAKU · GIFT", ru: "VINYL BAKU · ПОДАРОК" } },
      { key: "title", label: "Səhifə başlığı", multiline: false,
        defaults: { az: "Hədiyyə Kartı", en: "Gift Card", ru: "Подарочная карта" } },
      { key: "lede", label: "Qısa təsvir (başlığın altında)", multiline: true,
        defaults: {
          az: "Sevdikləriniz üçün mükəmməl hədiyyə — istənilən vinili seçmə azadlığı. Hədiyyə kartını alın, müştəriyə avtomatik unikal endirim kodu çatdırılacaq və o, kataloqumuzdakı istənilən məhsulu öz seçimi ilə əldə edə bilər.",
          en: "The perfect gift for your loved ones — the freedom to pick any vinyl. Buy a gift card and a unique discount code is delivered automatically, redeemable for any product in our catalog.",
          ru: "Идеальный подарок — свобода выбрать любую пластинку. Купите подарочную карту, и получатель получит уникальный код скидки на любой товар в каталоге.",
        } },
    ],
    images: [],
  },
  {
    key: "blog", title: "Yeniliklər (Jurnal)",
    desc: "Bloq / yeniliklər səhifəsinin başlığı və qısa təsviri.",
    fields: [
      { key: "eyebrow", label: "Üst yazı", multiline: false,
        defaults: { az: "JURNAL · YENİLİKLƏR", en: "JOURNAL · NEWS", ru: "ЖУРНАЛ · НОВОСТИ" } },
      { key: "title", label: "Səhifə başlığı", multiline: false,
        defaults: { az: "Yeniliklər", en: "News", ru: "Новости" } },
      { key: "lede", label: "Qısa təsvir", multiline: true,
        defaults: {
          az: "Vinil səhnəsindən qısa qeydlər, bələdçilər və mağazaya yeni daxil olan buraxılışlar.",
          en: "Short notes from the vinyl scene, guides and fresh arrivals at the store.",
          ru: "Короткие заметки со сцены винила, гиды и свежие поступления.",
        } },
    ],
    images: [],
  },
  {
    key: "about", title: "Haqqımızda səhifəsi",
    desc: "Haqqımızda səhifəsinin bütün bölmələri.",
    fields: [
      { key: "eyebrow", label: "Üst yazı (header)", multiline: false,
        defaults: { az: "VINYL BAKU AZ · 2019—", en: "VINYL BAKU AZ · 2019—", ru: "VINYL BAKU AZ · 2019—" } },
      { key: "title", label: "Əsas başlıq", multiline: true,
        defaults: {
          az: "Analoq ruh üçün kiçik, lakin diqqətli bir arxiv.",
          en: "A small, careful archive for the analog soul.",
          ru: "Маленький, но внимательный архив для аналоговой души.",
        } },
      { key: "lede", label: "Giriş paraqrafı", multiline: true,
        defaults: {
          az: "Vinyl Baku 2019-cu ildə sadə bir fikirlə başladı: şəhərdə dürüst, kurator yanaşması ilə seçilmiş bir vinil məkanı yaratmaq. Bu gün biz Bakının audiofil icması üçün nadir caz idxallarından modern elektronikaya qədər diqqətlə seçilmiş kataloqu saxlayırıq.",
          en: "Vinyl Baku started in 2019 with a simple idea: an honest, curated vinyl space in the city. Today we keep a carefully chosen catalog for Baku's audiophile community — from rare jazz imports to modern electronic.",
          ru: "Vinyl Baku появился в 2019 году с простой идеей — честное, кураторское винил-пространство в городе. Сегодня мы храним тщательно отобранный каталог для аудиофилов Баку.",
        } },
      { key: "story_eyebrow", label: "“Hekayə” üst yazısı", multiline: false,
        defaults: { az: "HEKAYƏ", en: "STORY", ru: "ИСТОРИЯ" } },
      { key: "story_title", label: "“Hekayə” başlığı", multiline: false,
        defaults: {
          az: "Bir dükkan deyil. Bir arxiv.",
          en: "Not a shop. An archive.",
          ru: "Не магазин. Архив.",
        } },
      { key: "story1", label: "Hekayə — 1-ci paraqraf", multiline: true,
        defaults: {
          az: "Bakıda audiofil keyfiyyətli, şəffaf mənşəli vinil tapmağın çətin olduğunu gördük. Bu yüzdən kiçik bir kolleksiyadan başlayaraq, ildə cəmi 200-dən az başlığı yenilədiyimiz bir kurator məkan qurduq.",
          en: "We saw how hard it was to find audiophile-quality, transparently sourced vinyl in Baku. So we started a small, curated space — refreshing fewer than 200 titles per year.",
          ru: "Мы увидели, как трудно найти в Баку винил аудиофильного качества с прозрачным происхождением. Поэтому начали с маленькой кураторской коллекции, обновляя менее 200 наименований в год.",
        } },
      { key: "story2", label: "Hekayə — 2-ci paraqraf", multiline: true,
        defaults: {
          az: "Bu gün Avropa, ABŞ və Yaponiya presləri ilə işləyir, hər diski şəxsən yoxlayır və yalnız qənaətbəxş nüsxələri rəfə qoyuruq. Eyni zamanda yerli kolleksiyaçılardan ikinci əl nüsxələri də qəbul edirik — hər biri dəqiq vəziyyət qeydi ilə.",
          en: "Today we work with European, US and Japanese pressings, inspect every disc personally and only shelve copies we are happy with. We also take second-hand vinyl from local collectors — each with a precise condition note.",
          ru: "Сегодня мы работаем с европейскими, американскими и японскими прессами, проверяем каждый диск лично и оставляем на полках только те, что нас устраивают. Также принимаем секонд-хенд от местных коллекционеров — с подробной оценкой состояния.",
        } },
      { key: "story3", label: "Hekayə — 3-cü paraqraf", multiline: true,
        defaults: {
          az: "Məqsəd sadədir: vinili layiq olduğu kimi tapmaq, dinləmək və saxlamaq.",
          en: "The goal is simple: to find, listen to and keep vinyl the way it deserves.",
          ru: "Цель проста: находить, слушать и хранить винил так, как он того заслуживает.",
        } },
      { key: "principles_eyebrow", label: "“Prinsiplər” üst yazısı", multiline: false,
        defaults: { az: "PRİNSİPLƏR", en: "PRINCIPLES", ru: "ПРИНЦИПЫ" } },
      { key: "principles_title", label: "“Prinsiplər” başlığı", multiline: false,
        defaults: { az: "İşə yanaşmamız.", en: "How we work.", ru: "Наш подход." } },
      { key: "cta_title", label: "Aşağıdakı CTA başlığı", multiline: true,
        defaults: {
          az: "Kataloqumuzu gözdən keçirin.",
          en: "Browse our catalog.",
          ru: "Посмотрите наш каталог.",
        } },
      { key: "cta_button", label: "CTA düymə mətni", multiline: false,
        defaults: { az: "Kataloqa bax", en: "Open catalog", ru: "В каталог" } },
    ],
    images: [
      { key: "hero_image_url", label: "Hero şəkli (istəyə bağlı)" },
    ],
  },
];

// Sidebar navigation
const SECTIONS = [
  { id: "brand",          label: "Brend / Loqo",        group: "Ümumi",       icon: Tag,
    desc: "Sayt adı və şüarı (loqo mətni)." },
  { id: "social",         label: "Sosial və Əlaqə",     group: "Ümumi",       icon: Share2,
    desc: "Footer-də göstərilən Instagram, TikTok, WhatsApp, telefon və e-poçt." },

  { id: "banners",        label: "Bannerlər",            group: "Ana səhifə",  icon: ImageIcon,
    desc: "Saytın yuxarısında tam ekranda görünən bannerlər (slayder)." },
  { id: "ambient",        label: "Oxuyan mahnı",         group: "Ana səhifə",  icon: Music,
    desc: "Saytın aşağısında çalan kiçik plyerin mahnısı." },
  { id: "editorial",      label: "Redaksiya blokları",   group: "Ana səhifə",  icon: Newspaper,
    desc: "“REDAKSİYA / EDITORIAL · Bu həftə” bölməsindəki kartlar." },
  { id: "featured-artist",label: "Seçilmiş sənətçi",     group: "Ana səhifə",  icon: Disc3,
    desc: "Ana səhifədə böyük portretli sənətçi və yan diskoqrafiya." },
  { id: "page-home",      label: "Ana — bölmə mətnləri", group: "Ana səhifə",  icon: Home,
    desc: "“Yeni gələnlər”, “Ən çox satılanlar”, “Oxumalı” başlıqları." },
  { id: "page-home_masthead", label: "Ana — yuxarı zolaq", group: "Ana səhifə",  icon: FileText,
    desc: "VOL. 47, tarix, ANALOG WEEKLY, axan yazı və “Bütün kataloq” linki." },

  { id: "page-catalog",   label: "Kataloq",              group: "Səhifələr",   icon: Grid3x3,
    desc: "Kataloq səhifəsinin başlığı." },
  { id: "page-artists",   label: "Sənətçilər",           group: "Səhifələr",   icon: User,
    desc: "Sənətçilər siyahısı səhifəsinin başlığı." },
  { id: "page-gift_card", label: "Hədiyyə Kartı",        group: "Səhifələr",   icon: Gift,
    desc: "Hədiyyə kartı satın alma səhifəsi." },
  { id: "page-blog",      label: "Yeniliklər",           group: "Səhifələr",   icon: Newspaper,
    desc: "Bloq / yeniliklər səhifəsinin başlığı və qısa təsviri." },
  { id: "page-about",     label: "Haqqımızda",           group: "Səhifələr",   icon: Info,
    desc: "Haqqımızda səhifəsinin bütün bölmələri." },

  { id: "footer",         label: "Footer mətnləri",      group: "Footer & Arxiv", icon: FileText,
    desc: "Saytın altındakı haqqımızda, ünvan, copyright və şüar." },
  { id: "backup-hero",    label: "Ehtiyat hero",         group: "Footer & Arxiv", icon: Archive,
    desc: "Banner yoxdursa istifadə olunan ehtiyat başlıq və şəkil." },
];

// ─── Helpers ────────────────────────────────────────────────────────────
function emptyMl() { return { az: "", en: "", ru: "" }; }
function newBanner() {
  return {
    id: `b_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
    image_url: "", video_url: "", video_url_mobile: "",
    eyebrow: emptyMl(), title: emptyMl(), desc: emptyMl(), cta_label: emptyMl(),
    cta_url: "/catalog", align: "left", overlay: "dark",
  };
}
function newEditorial() {
  return {
    id: `e_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
    image_url: "", eyebrow: emptyMl(), title: emptyMl(), desc: emptyMl(),
    cta_label: emptyMl(), cta_url: "/catalog", stat_value: "",
    stat_label: emptyMl(), icon: "vinyl", align: "left",
  };
}

// ────────────────────────────────────────────────────────────────────────
export default function AdminContent() {
  const { reloadContent } = useApp();
  const [data, setData] = useState({
    brand: { name: emptyMl(), tagline: emptyMl() },
    hero: { eyebrow: emptyMl(), title: emptyMl(), desc: emptyMl(), image_url: "" },
    banners: [],
    editorial_blocks: [],
    ambient_player: { title: emptyMl(), artist: emptyMl(), src: "" },
    featured_artist_id: "",
    discography_artist_ids: [],
    footer: { about: emptyMl(), address: emptyMl(), copyright: emptyMl(), tagline: emptyMl() },
    social: { instagram_url: "", tiktok_url: "", whatsapp_url: "", phone: "", email: "" },
    pages: {},
  });
  const [savedSnapshot, setSavedSnapshot] = useState(null);
  const [tab, setTab] = useState("az");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [uploadingId, setUploadingId] = useState(null);
  const [uploadingHero, setUploadingHero] = useState(false);
  const [uploadingAudio, setUploadingAudio] = useState(false);
  const [openIdx, setOpenIdx] = useState(0);
  const [openEditorialIdx, setOpenEditorialIdx] = useState(0);
  const [artistList, setArtistList] = useState([]);
  const [activeSection, setActiveSection] = useState("banners");
  const [search, setSearch] = useState("");

  // Load artists
  useEffect(() => {
    listAll("artists").then((rows) => {
      rows.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
      setArtistList(rows);
    }).catch(() => setArtistList([]));
  }, []);

  // Load content
  useEffect(() => {
    getContent("site").then((c) => {
      const ensureMl = (v) => (typeof v === "object" && v ? { az: "", en: "", ru: "", ...v } : { az: v || "", en: "", ru: "" });
      const ensureBanner = (b) => ({
        id: b.id || `b_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
        image_url: b.image_url || "", video_url: b.video_url || "", video_url_mobile: b.video_url_mobile || "",
        eyebrow: ensureMl(b.eyebrow), title: ensureMl(b.title), desc: ensureMl(b.desc),
        cta_label: ensureMl(b.cta_label), cta_url: b.cta_url || "/catalog",
        align: b.align || "left", overlay: b.overlay || "dark",
      });
      const ensureEditorial = (b) => ({
        id: b.id || `e_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
        image_url: b.image_url || "",
        eyebrow: ensureMl(b.eyebrow), title: ensureMl(b.title), desc: ensureMl(b.desc),
        cta_label: ensureMl(b.cta_label), cta_url: b.cta_url || "/catalog",
        stat_value: b.stat_value || "", stat_label: ensureMl(b.stat_label),
        icon: b.icon || "vinyl", align: b.align || "left",
      });
      const next = {
        brand: c?.brand ? { name: ensureMl(c.brand.name), tagline: ensureMl(c.brand.tagline) } : { name: emptyMl(), tagline: emptyMl() },
        hero: c?.hero ? {
          eyebrow: ensureMl(c.hero.eyebrow), title: ensureMl(c.hero.title), desc: ensureMl(c.hero.desc),
          image_url: c.hero.image_url || "",
        } : { eyebrow: emptyMl(), title: emptyMl(), desc: emptyMl(), image_url: "" },
        banners: Array.isArray(c?.banners) ? c.banners.map(ensureBanner) : [],
        editorial_blocks: Array.isArray(c?.editorial_blocks) ? c.editorial_blocks.map(ensureEditorial) : [],
        ambient_player: c?.ambient_player ? {
          title: ensureMl(c.ambient_player.title), artist: ensureMl(c.ambient_player.artist),
          src: c.ambient_player.src || "",
        } : { title: emptyMl(), artist: emptyMl(), src: "" },
        featured_artist_id: c?.featured_artist_id || "",
        discography_artist_ids: Array.isArray(c?.discography_artist_ids) ? c.discography_artist_ids : [],
        footer: c?.footer ? {
          about: ensureMl(c.footer.about), address: ensureMl(c.footer.address),
          copyright: ensureMl(c.footer.copyright), tagline: ensureMl(c.footer.tagline),
        } : { about: emptyMl(), address: emptyMl(), copyright: emptyMl(), tagline: emptyMl() },
        social: c?.social || { instagram_url: "", tiktok_url: "", whatsapp_url: "", phone: "", email: "" },
        pages: (() => {
          const out = {};
          for (const p of PAGE_SCHEMA) {
            out[p.key] = {};
            for (const f of p.fields) {
              const saved = c?.pages?.[p.key]?.[f.key];
              out[p.key][f.key] = ensureMl(saved !== undefined && saved !== "" ? saved : f.defaults);
            }
            for (const im of p.images) {
              out[p.key][im.key] = c?.pages?.[p.key]?.[im.key] || "";
            }
          }
          return out;
        })(),
      };
      setData(next);
      setSavedSnapshot(JSON.stringify(next));
    });
  }, []);

  // Dirty tracking
  const dirty = useMemo(() => savedSnapshot !== null && JSON.stringify(data) !== savedSnapshot, [data, savedSnapshot]);

  const save = async () => {
    setBusy(true); setMsg("");
    try {
      await setContent("site", data);
      await reloadContent();
      setSavedSnapshot(JSON.stringify(data));
      setMsg("Yadda saxlandı. ✓");
    } catch (e) { setMsg(e.message || "Xəta."); }
    finally { setBusy(false); setTimeout(() => setMsg(""), 3000); }
  };

  const onUploadHero = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setUploadingHero(true);
    try {
      const url = await uploadImageToR2(f, { folder: "site" });
      setData((d) => ({ ...d, hero: { ...d.hero, image_url: url } }));
    } catch (ex) { alert(ex.message); }
    finally { setUploadingHero(false); e.target.value = ""; }
  };

  const onUploadBanner = async (idx, e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setUploadingId(idx);
    try {
      const url = await uploadImageToR2(f, { folder: "banners" });
      setData((d) => {
        const next = [...d.banners];
        next[idx] = { ...next[idx], image_url: url };
        return { ...d, banners: next };
      });
    } catch (ex) { alert(ex.message); }
    finally { setUploadingId(null); e.target.value = ""; }
  };

  const onUploadBannerVideo = async (idx, e, field = "video_url") => {
    const f = e.target.files?.[0]; if (!f) return;
    setUploadingId(`${field}_${idx}`);
    try {
      const url = await uploadImageToR2(f, { folder: "banners" });
      setData((d) => {
        const next = [...d.banners];
        next[idx] = { ...next[idx], [field]: url };
        return { ...d, banners: next };
      });
    } catch (ex) { alert(ex.message); }
    finally { setUploadingId(null); e.target.value = ""; }
  };

  const updateBanner = (idx, key, value) => {
    setData((d) => {
      const next = [...d.banners];
      next[idx] = { ...next[idx], [key]: value };
      return { ...d, banners: next };
    });
  };
  const updateBannerMl = (idx, key, value) => {
    setData((d) => {
      const next = [...d.banners];
      next[idx] = { ...next[idx], [key]: { ...next[idx][key], [tab]: value } };
      return { ...d, banners: next };
    });
  };
  const addBanner = () => {
    setData((d) => ({ ...d, banners: [...d.banners, newBanner()] }));
    setOpenIdx(data.banners.length);
  };
  const removeBanner = (idx) => {
    if (!window.confirm("Bu banneri silməyə əminsən?")) return;
    setData((d) => ({ ...d, banners: d.banners.filter((_, i) => i !== idx) }));
  };
  const moveBanner = (idx, dir) => {
    setData((d) => {
      const next = [...d.banners];
      const target = idx + dir;
      if (target < 0 || target >= next.length) return d;
      [next[idx], next[target]] = [next[target], next[idx]];
      return { ...d, banners: next };
    });
  };

  // Editorial helpers
  const updateEditorial = (idx, key, value) => {
    setData((d) => {
      const next = [...(d.editorial_blocks || [])];
      next[idx] = { ...next[idx], [key]: value };
      return { ...d, editorial_blocks: next };
    });
  };
  const updateEditorialMl = (idx, key, value) => {
    setData((d) => {
      const next = [...(d.editorial_blocks || [])];
      next[idx] = { ...next[idx], [key]: { ...next[idx][key], [tab]: value } };
      return { ...d, editorial_blocks: next };
    });
  };
  const addEditorial = () => {
    setData((d) => ({ ...d, editorial_blocks: [...(d.editorial_blocks || []), newEditorial()] }));
    setOpenEditorialIdx((data.editorial_blocks || []).length);
  };
  const removeEditorial = (idx) => {
    if (!window.confirm("Bu redaksiya blokunu silməyə əminsən?")) return;
    setData((d) => ({ ...d, editorial_blocks: (d.editorial_blocks || []).filter((_, i) => i !== idx) }));
  };
  const moveEditorial = (idx, dir) => {
    setData((d) => {
      const next = [...(d.editorial_blocks || [])];
      const target = idx + dir;
      if (target < 0 || target >= next.length) return d;
      [next[idx], next[target]] = [next[target], next[idx]];
      return { ...d, editorial_blocks: next };
    });
  };
  const onUploadEditorial = async (idx, e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setUploadingId(`editorial_${idx}`);
    try {
      const url = await uploadImageToR2(f, { folder: "editorial" });
      updateEditorial(idx, "image_url", url);
    } catch (ex) { alert(ex.message); }
    finally { setUploadingId(null); e.target.value = ""; }
  };

  // Ambient
  const updateAmbientMl = (key, value) => {
    setData((d) => ({
      ...d,
      ambient_player: { ...d.ambient_player, [key]: { ...d.ambient_player[key], [tab]: value } },
    }));
  };
  const onUploadAudio = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setUploadingAudio(true);
    try {
      const url = await uploadImageToR2(f, { folder: "audio" });
      setData((d) => ({ ...d, ambient_player: { ...d.ambient_player, src: url } }));
    } catch (ex) { alert(ex.message); }
    finally { setUploadingAudio(false); e.target.value = ""; }
  };

  // Featured artist
  const toggleDiscographyArtist = (artistId) => {
    setData((d) => {
      const ids = Array.isArray(d.discography_artist_ids) ? d.discography_artist_ids : [];
      const idx = ids.indexOf(artistId);
      let next;
      if (idx >= 0) next = ids.filter((id) => id !== artistId);
      else if (ids.length >= 4) next = ids;
      else next = [...ids, artistId];
      return { ...d, discography_artist_ids: next };
    });
  };

  // Pages
  const updatePageField = (pageKey, fieldKey, value) => {
    setData((d) => ({
      ...d,
      pages: {
        ...d.pages,
        [pageKey]: {
          ...(d.pages?.[pageKey] || {}),
          [fieldKey]: { ...((d.pages?.[pageKey] || {})[fieldKey] || {}), [tab]: value },
        },
      },
    }));
  };
  const updatePageImage = (pageKey, key, value) => {
    setData((d) => ({
      ...d,
      pages: { ...d.pages, [pageKey]: { ...(d.pages?.[pageKey] || {}), [key]: value } },
    }));
  };
  const onUploadPageImage = async (pageKey, key, e) => {
    const f = e.target.files?.[0]; if (!f) return;
    const id = `${pageKey}_${key}`;
    setUploadingId(id);
    try {
      const url = await uploadImageToR2(f, { folder: `pages/${pageKey}` });
      updatePageImage(pageKey, key, url);
    } catch (ex) { alert(ex.message); }
    finally { setUploadingId(null); e.target.value = ""; }
  };

  // ─── Sidebar grouping with search ────────────────────────────────────
  const groupedSections = useMemo(() => {
    const q = search.trim().toLowerCase();
    const filtered = SECTIONS.filter(
      (s) => !q || s.label.toLowerCase().includes(q) || s.desc.toLowerCase().includes(q) || s.group.toLowerCase().includes(q),
    );
    const groups = {};
    for (const s of filtered) {
      if (!groups[s.group]) groups[s.group] = [];
      groups[s.group].push(s);
    }
    return Object.entries(groups).map(([name, items]) => ({ name, items }));
  }, [search]);

  const active = SECTIONS.find((s) => s.id === activeSection) || SECTIONS[0];

  // ─── Render helpers ──────────────────────────────────────────────────
  const SectionHeader = ({ icon: Icon, title, desc, action }) => (
    <div className="mb-7 flex items-start justify-between gap-6">
      <div className="flex items-start gap-4 min-w-0">
        <div className="w-11 h-11 border border-[var(--border)] flex items-center justify-center flex-shrink-0">
          <Icon size={18}/>
        </div>
        <div className="min-w-0">
          <div className="text-[11px] tracking-[0.04em] text-[var(--text-muted)] font-medium">{active.group}</div>
          <h2 className="font-display text-3xl mt-0.5">{title}</h2>
          {desc && <p className="text-sm text-[var(--text-2)] mt-2 max-w-2xl">{desc}</p>}
        </div>
      </div>
      {action}
    </div>
  );

  // Page editor render (one of the PAGE_SCHEMA entries)
  const renderPageSection = (pageKey) => {
    const page = PAGE_SCHEMA.find((p) => p.key === pageKey);
    if (!page) return null;
    const pageData = data.pages?.[pageKey] || {};
    return (
      <div>
        <SectionHeader icon={active.icon} title={page.title} desc={page.desc}/>
        <div className="space-y-5">
          {page.fields.map((f) => (
            <div key={f.key}>
              <label className="text-[11px] tracking-[0.04em] text-[var(--text-muted)] font-medium">{f.label}</label>
              {f.multiline ? (
                <textarea
                  rows={3}
                  className="w-full bg-transparent border border-[var(--border)] px-3 py-2 mt-1"
                  value={pageData[f.key]?.[tab] || ""}
                  onChange={(e) => updatePageField(pageKey, f.key, e.target.value)}
                  data-testid={`page-${pageKey}-${f.key}`}
                />
              ) : (
                <input
                  className="w-full bg-transparent border border-[var(--border)] px-3 py-2 mt-1"
                  value={pageData[f.key]?.[tab] || ""}
                  onChange={(e) => updatePageField(pageKey, f.key, e.target.value)}
                  data-testid={`page-${pageKey}-${f.key}`}
                />
              )}
            </div>
          ))}
          {page.images.map((im) => {
            const url = pageData[im.key] || "";
            const upId = `${pageKey}_${im.key}`;
            return (
              <div key={im.key}>
                <label className="text-[11px] tracking-[0.04em] text-[var(--text-muted)] font-medium">{im.label}</label>
                <div className="flex items-center gap-3 mt-1">
                  {url && <img src={url} alt="" className="w-24 h-24 object-cover border border-[var(--border)]"/>}
                  <label className="btn-outline cursor-pointer" style={{ textTransform: "none", letterSpacing: "0" }}>
                    <Upload size={14}/> {uploadingId === upId ? "Yüklənir…" : "Yüklə"}
                    <input type="file" accept="image/*" hidden onChange={(e) => onUploadPageImage(pageKey, im.key, e)} data-testid={`page-${pageKey}-${im.key}-upload`}/>
                  </label>
                  <input
                    className="flex-1 bg-transparent border border-[var(--border)] px-3 py-2 font-mono text-xs"
                    placeholder="və ya şəkil URL-i"
                    value={url}
                    onChange={(e) => updatePageImage(pageKey, im.key, e.target.value)}
                    data-testid={`page-${pageKey}-${im.key}`}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // ─── Section renderers ───────────────────────────────────────────────
  const renderBrand = () => (
    <div>
      <SectionHeader icon={Tag} title="Brend / Loqo" desc="Sayt başlığı (loqo) və şüarı."/>
      <div className="space-y-5 max-w-2xl">
        {FIELDS[0].items.map((it) => (
          <div key={it.key}>
            <label className="text-[11px] tracking-[0.04em] text-[var(--text-muted)] font-medium">{it.label}</label>
            {it.multiline ? (
              <textarea rows={3} className="w-full bg-transparent border border-[var(--border)] px-3 py-2 mt-1"
                value={data.brand?.[it.key]?.[tab] || ""}
                onChange={(e) => setData({ ...data, brand: { ...data.brand, [it.key]: { ...data.brand[it.key], [tab]: e.target.value } } })}
                data-testid={`f-brand-${it.key}`}/>
            ) : (
              <input className="w-full bg-transparent border border-[var(--border)] px-3 py-2 mt-1"
                value={data.brand?.[it.key]?.[tab] || ""}
                onChange={(e) => setData({ ...data, brand: { ...data.brand, [it.key]: { ...data.brand[it.key], [tab]: e.target.value } } })}
                data-testid={`f-brand-${it.key}`}/>
            )}
          </div>
        ))}
      </div>
    </div>
  );

  const renderSocial = () => (
    <div>
      <SectionHeader icon={Share2} title="Sosial və Əlaqə" desc="Saytın altında (footer) görünür."/>
      <div className="grid sm:grid-cols-2 gap-4 max-w-3xl">
        {[
          ["instagram_url", "Instagram URL",  "https://instagram.com/vinylbaku"],
          ["tiktok_url",    "TikTok URL",     "https://tiktok.com/@vinylbaku"],
          ["whatsapp_url",  "WhatsApp URL",   "https://wa.me/994501234567"],
          ["phone",         "Telefon",        "+994 50 123 45 67"],
          ["email",         "E-poçt",         "info@vinylbaku.az"],
        ].map(([k, label, ph]) => (
          <div key={k}>
            <label className="text-[11px] tracking-[0.04em] text-[var(--text-muted)] font-medium">{label}</label>
            <input
              className="w-full bg-transparent border border-[var(--border)] px-3 py-2 mt-1 font-mono text-sm"
              placeholder={ph}
              value={data.social?.[k] || ""}
              onChange={(e) => setData({ ...data, social: { ...data.social, [k]: e.target.value } })}
              data-testid={`f-social-${k}`}
            />
          </div>
        ))}
      </div>
    </div>
  );

  const renderBanners = () => (
    <div>
      <SectionHeader
        icon={ImageIcon}
        title="Bannerlər"
        desc="Ana səhifədə tam ekran kimi görünən slayder. Hər banner üçün şəkil/video və 3 dildə mətn."
        action={
          <button onClick={addBanner} className="btn-outline" style={{ textTransform: "none", letterSpacing: "0" }} data-testid="add-banner">
            <Plus size={14}/> Yeni banner
          </button>
        }
      />
      {data.banners.length === 0 && (
        <div className="border border-dashed border-[var(--border)] py-14 text-center text-[var(--text-muted)] text-sm">
          Hələ banner əlavə edilməyib. <button onClick={addBanner} className="underline">İlk banner əlavə et</button>
        </div>
      )}
      <div className="space-y-3">
        {data.banners.map((b, idx) => {
          const isOpen = openIdx === idx;
          return (
            <div key={b.id || idx} className="border border-[var(--border)]" data-testid={`banner-${idx}`}>
              <div className="flex items-center gap-4 p-4">
                <div className="w-20 h-12 bg-[var(--border-soft)] flex-shrink-0 overflow-hidden">
                  {b.image_url && <img src={b.image_url} alt="" className="w-full h-full object-cover"/>}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="metadata text-[var(--text-muted)]">Banner {String(idx + 1).padStart(2, "0")}</div>
                  <div className="font-display text-base mt-0.5 truncate">
                    {b.title?.[tab] || b.title?.az || <span className="text-[var(--text-muted)]">(Başlıq yoxdur)</span>}
                  </div>
                </div>
                <button onClick={() => moveBanner(idx, -1)} disabled={idx === 0} className="btn-ghost disabled:opacity-30" aria-label="Yuxarı"><ChevronUp size={14}/></button>
                <button onClick={() => moveBanner(idx, 1)} disabled={idx === data.banners.length - 1} className="btn-ghost disabled:opacity-30" aria-label="Aşağı"><ChevronDown size={14}/></button>
                <button onClick={() => setOpenIdx(isOpen ? -1 : idx)} className="btn-ghost" style={{ textTransform: "none", letterSpacing: "0.02em" }} data-testid={`banner-toggle-${idx}`}>
                  {isOpen ? "Bağla" : "Redaktə"}
                </button>
                <button onClick={() => removeBanner(idx)} className="btn-ghost text-[var(--accent)] hover:!text-white hover:!bg-[var(--accent)] hover:!border-[var(--accent)]" aria-label="Sil"><Trash2 size={14}/></button>
              </div>
              {isOpen && (
                <div className="p-5 border-t border-[var(--border)] space-y-5">
                  <div>
                    <label className="metadata text-[var(--text-muted)]">Masaüstü videosu (geniş ekran)</label>
                    <div className="flex items-start gap-4 mt-2">
                      <div className="w-48 h-28 border border-[var(--border)] overflow-hidden flex-shrink-0 bg-black/5 flex items-center justify-center">
                        {b.video_url ? <video src={b.video_url} className="w-full h-full object-cover" muted autoPlay loop playsInline /> : <div className="text-[var(--text-muted)] text-xs">Video yoxdur</div>}
                      </div>
                      <div className="flex-1 space-y-2">
                        <div className="flex items-center gap-2 flex-wrap">
                          <label className="btn-outline cursor-pointer inline-flex">
                            <Upload size={14}/> {uploadingId === `video_url_${idx}` ? "Yüklənir…" : "Masaüstü videosunu yüklə"}
                            <input type="file" accept="video/*" hidden onChange={(e) => onUploadBannerVideo(idx, e, "video_url")} data-testid={`banner-${idx}-upload-video-desktop`}/>
                          </label>
                          {b.video_url && (
                            <button type="button" onClick={() => updateBanner(idx, "video_url", "")} className="btn-ghost text-[var(--accent)]"><Trash2 size={14}/> Sil</button>
                          )}
                        </div>
                        <input className="w-full bg-transparent border border-[var(--border)] px-3 py-2 font-mono text-xs"
                          placeholder="və ya video URL-i" value={b.video_url}
                          onChange={(e) => updateBanner(idx, "video_url", e.target.value)}/>
                        <p className="text-xs text-[var(--text-muted)] leading-snug">Tövsiyə: mp4 / h264, ≥1920×1080, &lt;20MB.</p>
                      </div>
                    </div>
                  </div>
                  <div>
                    <label className="metadata text-[var(--text-muted)]">Mobil videosu (şaquli / 9:16)</label>
                    <div className="flex items-start gap-4 mt-2">
                      <div className="w-20 h-32 border border-[var(--border)] overflow-hidden flex-shrink-0 bg-black/5 flex items-center justify-center">
                        {b.video_url_mobile ? <video src={b.video_url_mobile} className="w-full h-full object-cover" muted autoPlay loop playsInline /> : <div className="text-[var(--text-muted)] text-xs px-2 text-center">Mobil video yoxdur</div>}
                      </div>
                      <div className="flex-1 space-y-2">
                        <div className="flex items-center gap-2 flex-wrap">
                          <label className="btn-outline cursor-pointer inline-flex">
                            <Upload size={14}/> {uploadingId === `video_url_mobile_${idx}` ? "Yüklənir…" : "Mobil video yüklə"}
                            <input type="file" accept="video/*" hidden onChange={(e) => onUploadBannerVideo(idx, e, "video_url_mobile")}/>
                          </label>
                          {b.video_url_mobile && (
                            <button type="button" onClick={() => updateBanner(idx, "video_url_mobile", "")} className="btn-ghost text-[var(--accent)]"><Trash2 size={14}/> Sil</button>
                          )}
                        </div>
                        <input className="w-full bg-transparent border border-[var(--border)] px-3 py-2 font-mono text-xs"
                          placeholder="və ya mobil video URL-i" value={b.video_url_mobile}
                          onChange={(e) => updateBanner(idx, "video_url_mobile", e.target.value)}/>
                        <p className="text-xs text-[var(--text-muted)] leading-snug">Mobildə bu video oynadılır. Boş qoyulsa desktop video istifadə edilir.</p>
                      </div>
                    </div>
                  </div>
                  <div>
                    <label className="metadata text-[var(--text-muted)]">Banner şəkli (video yoxdursa)</label>
                    <div className="flex items-start gap-4 mt-2">
                      <div className="w-48 h-28 border border-[var(--border)] overflow-hidden flex-shrink-0 bg-black/5">
                        {b.image_url ? <img src={b.image_url} alt="" className="w-full h-full object-cover"/> : <div className="w-full h-full flex items-center justify-center text-[var(--text-muted)] text-xs">Şəkil yoxdur</div>}
                      </div>
                      <div className="flex-1 space-y-2">
                        <label className="btn-outline cursor-pointer inline-flex">
                          <Upload size={14}/> {uploadingId === idx ? "Yüklənir…" : "Şəkil yüklə"}
                          <input type="file" accept="image/*" hidden onChange={(e) => onUploadBanner(idx, e)}/>
                        </label>
                        <input className="w-full bg-transparent border border-[var(--border)] px-3 py-2 font-mono text-xs"
                          placeholder="və ya şəkil URL-i" value={b.image_url}
                          onChange={(e) => updateBanner(idx, "image_url", e.target.value)}/>
                      </div>
                    </div>
                  </div>
                  <div>
                    <label className="metadata text-[var(--text-muted)]">Üst yazı (eyebrow) — {tab.toUpperCase()}</label>
                    <input className="w-full bg-transparent border border-[var(--border)] px-3 py-2 mt-1"
                      placeholder="məs: PREMİUM SEÇİM — BAKI, AZ"
                      value={b.eyebrow?.[tab] || ""} onChange={(e) => updateBannerMl(idx, "eyebrow", e.target.value)}
                      data-testid={`banner-${idx}-eyebrow`}/>
                  </div>
                  <div>
                    <label className="metadata text-[var(--text-muted)]">Başlıq — {tab.toUpperCase()} (sətir keçidi üçün Enter)</label>
                    <textarea rows={3} className="w-full bg-transparent border border-[var(--border)] px-3 py-2 mt-1 font-display text-xl"
                      placeholder="məs: Analoq ruh,&#10;rəqəmsal dünyada."
                      value={b.title?.[tab] || ""} onChange={(e) => updateBannerMl(idx, "title", e.target.value)}
                      data-testid={`banner-${idx}-title`}/>
                  </div>
                  <div>
                    <label className="metadata text-[var(--text-muted)]">Təsvir — {tab.toUpperCase()}</label>
                    <textarea rows={2} className="w-full bg-transparent border border-[var(--border)] px-3 py-2 mt-1"
                      value={b.desc?.[tab] || ""} onChange={(e) => updateBannerMl(idx, "desc", e.target.value)}/>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="metadata text-[var(--text-muted)]">Düymə mətni — {tab.toUpperCase()}</label>
                      <input className="w-full bg-transparent border border-[var(--border)] px-3 py-2 mt-1"
                        placeholder="məs: Kataloqa bax" value={b.cta_label?.[tab] || ""}
                        onChange={(e) => updateBannerMl(idx, "cta_label", e.target.value)}/>
                    </div>
                    <div>
                      <label className="metadata text-[var(--text-muted)]">Düymə linki</label>
                      <input className="w-full bg-transparent border border-[var(--border)] px-3 py-2 mt-1 font-mono text-xs"
                        placeholder="/catalog" value={b.cta_url || ""}
                        onChange={(e) => updateBanner(idx, "cta_url", e.target.value)}/>
                    </div>
                  </div>
                  <div>
                    <label className="metadata text-[var(--text-muted)]">Mətn mövqeyi</label>
                    <select className="w-full bg-transparent border border-[var(--border)] px-3 py-2 mt-1"
                      value={b.align} onChange={(e) => updateBanner(idx, "align", e.target.value)}>
                      {ALIGNS.map((a) => <option key={a.v} value={a.v}>{a.l}</option>)}
                    </select>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );

  const renderAmbient = () => (
    <div data-testid="ambient-editor">
      <SectionHeader icon={Music} title="Oxuyan mahnı (Ambient Player)" desc="Saytın aşağısında çalan kiçik plyerin mahnısı. Yenilədikdən sonra dərhal saytda eşidilir."/>
      <div className="space-y-5 max-w-2xl">
        <div>
          <label className="text-[11px] tracking-[0.04em] text-[var(--text-muted)] font-medium">Mahnı adı ({tab.toUpperCase()})</label>
          <input className="w-full bg-transparent border border-[var(--border)] px-3 py-2 mt-1"
            placeholder="məs: İntizar (Vəfadarım Mənim)"
            value={data.ambient_player?.title?.[tab] || ""}
            onChange={(e) => updateAmbientMl("title", e.target.value)} data-testid="ambient-title"/>
        </div>
        <div>
          <label className="text-[11px] tracking-[0.04em] text-[var(--text-muted)] font-medium">Sənətçi ({tab.toUpperCase()})</label>
          <input className="w-full bg-transparent border border-[var(--border)] px-3 py-2 mt-1"
            placeholder="məs: Elmira Rəhimova"
            value={data.ambient_player?.artist?.[tab] || ""}
            onChange={(e) => updateAmbientMl("artist", e.target.value)} data-testid="ambient-artist"/>
        </div>
        <div>
          <label className="text-[11px] tracking-[0.04em] text-[var(--text-muted)] font-medium">Mahnı faylı</label>
          <div className="flex items-start gap-4 mt-2">
            <div className="w-40 h-20 border border-[var(--border)] flex items-center justify-center bg-black/5 flex-shrink-0 p-2">
              {data.ambient_player?.src
                ? <audio src={data.ambient_player.src} controls className="w-full"/>
                : <div className="text-[var(--text-muted)] text-xs">Audio yoxdur</div>}
            </div>
            <div className="flex-1 space-y-2">
              <div className="flex items-center gap-2 flex-wrap">
                <label className="btn-outline cursor-pointer inline-flex" style={{ textTransform: "none", letterSpacing: "0" }}>
                  <Upload size={14}/> {uploadingAudio ? "Yüklənir…" : "Audio yüklə (mp3, m4a, wav)"}
                  <input type="file" accept="audio/*" hidden onChange={onUploadAudio} data-testid="ambient-upload-audio"/>
                </label>
                {data.ambient_player?.src && (
                  <button type="button" onClick={() => setData((d) => ({ ...d, ambient_player: { ...d.ambient_player, src: "" } }))}
                    className="btn-ghost text-[var(--accent)]" data-testid="ambient-remove-audio">
                    <Trash2 size={14}/> Sil
                  </button>
                )}
              </div>
              <input className="w-full bg-transparent border border-[var(--border)] px-3 py-2 font-mono text-xs"
                placeholder="və ya birbaşa audio URL-i (mp3/m4a)"
                value={data.ambient_player?.src || ""}
                onChange={(e) => setData((d) => ({ ...d, ambient_player: { ...d.ambient_player, src: e.target.value } }))}
                data-testid="ambient-src"/>
              <p className="text-[11px] text-[var(--text-muted)] leading-snug">Tövsiyə: mp3 / 128-320 kbps, &lt;8 MB. Sayt avtomatik səssiz oynadır.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderEditorial = () => (
    <div>
      <SectionHeader
        icon={Newspaper}
        title="Redaksiya blokları (Bu həftə)"
        desc="Ana səhifədəki “REDAKSİYA · Bu həftə” bölməsi. Hər blok növbə ilə sol və sağ kompozisiyada görsənir."
        action={
          <button onClick={addEditorial} className="btn-outline" style={{ textTransform: "none", letterSpacing: "0" }} data-testid="add-editorial">
            <Plus size={14}/> Yeni blok
          </button>
        }
      />
      {(data.editorial_blocks || []).length === 0 && (
        <div className="border border-dashed border-[var(--border)] py-14 text-center text-[var(--text-muted)] text-sm">
          Hələ blok əlavə edilməyib. <button onClick={addEditorial} className="underline">İlk bloku əlavə et</button>
        </div>
      )}
      <div className="space-y-3">
        {(data.editorial_blocks || []).map((b, idx) => {
          const isOpen = openEditorialIdx === idx;
          return (
            <div key={b.id || idx} className="border border-[var(--border)]" data-testid={`editorial-${idx}`}>
              <div className="flex items-center gap-4 p-4">
                <div className="w-20 h-14 bg-[var(--border-soft)] flex-shrink-0 overflow-hidden">
                  {b.image_url && <img src={b.image_url} alt="" className="w-full h-full object-cover"/>}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="metadata text-[var(--text-muted)]">Blok {String(idx + 1).padStart(2, "0")} · {b.align === "right" ? "Sağ" : "Sol"}</div>
                  <div className="font-display text-base mt-0.5 truncate">
                    {b.title?.[tab] || b.title?.az || <span className="text-[var(--text-muted)]">(Başlıq yoxdur)</span>}
                  </div>
                </div>
                <button onClick={() => moveEditorial(idx, -1)} disabled={idx === 0} className="btn-ghost disabled:opacity-30"><ChevronUp size={14}/></button>
                <button onClick={() => moveEditorial(idx, 1)} disabled={idx === (data.editorial_blocks || []).length - 1} className="btn-ghost disabled:opacity-30"><ChevronDown size={14}/></button>
                <button onClick={() => setOpenEditorialIdx(isOpen ? -1 : idx)} className="btn-ghost" style={{ textTransform: "none", letterSpacing: "0.02em" }} data-testid={`editorial-toggle-${idx}`}>
                  {isOpen ? "Bağla" : "Redaktə"}
                </button>
                <button onClick={() => removeEditorial(idx)} className="btn-ghost text-[var(--accent)] hover:!text-white hover:!bg-[var(--accent)] hover:!border-[var(--accent)]"><Trash2 size={14}/></button>
              </div>
              {isOpen && (
                <div className="p-5 border-t border-[var(--border)] space-y-5">
                  <div>
                    <label className="metadata text-[var(--text-muted)]">Şəkil</label>
                    <div className="flex items-start gap-4 mt-2">
                      <div className="w-48 h-32 border border-[var(--border)] overflow-hidden flex-shrink-0 bg-black/5">
                        {b.image_url ? <img src={b.image_url} alt="" className="w-full h-full object-cover"/> : <div className="w-full h-full flex items-center justify-center text-[var(--text-muted)] text-xs">Şəkil yoxdur</div>}
                      </div>
                      <div className="flex-1 space-y-2">
                        <label className="btn-outline cursor-pointer inline-flex" style={{ textTransform: "none", letterSpacing: "0" }}>
                          <Upload size={14}/> {uploadingId === `editorial_${idx}` ? "Yüklənir…" : "Şəkil yüklə"}
                          <input type="file" accept="image/*" hidden onChange={(e) => onUploadEditorial(idx, e)}/>
                        </label>
                        <input className="w-full bg-transparent border border-[var(--border)] px-3 py-2 font-mono text-xs"
                          placeholder="və ya şəkil URL-i" value={b.image_url}
                          onChange={(e) => updateEditorial(idx, "image_url", e.target.value)}/>
                      </div>
                    </div>
                  </div>
                  <div>
                    <label className="metadata text-[var(--text-muted)]">Üst yazı (eyebrow) — {tab.toUpperCase()}</label>
                    <input className="w-full bg-transparent border border-[var(--border)] px-3 py-2 mt-1"
                      placeholder="məs: KOLEKSİYA" value={b.eyebrow?.[tab] || ""}
                      onChange={(e) => updateEditorialMl(idx, "eyebrow", e.target.value)}/>
                  </div>
                  <div>
                    <label className="metadata text-[var(--text-muted)]">Başlıq — {tab.toUpperCase()} (sətir keçidi üçün Enter)</label>
                    <textarea rows={2} className="w-full bg-transparent border border-[var(--border)] px-3 py-2 mt-1 font-display text-xl"
                      placeholder="Vinyl səsi —&#10;zamana qarşı"
                      value={b.title?.[tab] || ""} onChange={(e) => updateEditorialMl(idx, "title", e.target.value)}/>
                  </div>
                  <div>
                    <label className="metadata text-[var(--text-muted)]">Təsvir — {tab.toUpperCase()}</label>
                    <textarea rows={3} className="w-full bg-transparent border border-[var(--border)] px-3 py-2 mt-1"
                      value={b.desc?.[tab] || ""} onChange={(e) => updateEditorialMl(idx, "desc", e.target.value)}/>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="metadata text-[var(--text-muted)]">Düymə mətni — {tab.toUpperCase()}</label>
                      <input className="w-full bg-transparent border border-[var(--border)] px-3 py-2 mt-1"
                        placeholder="KATALOQU AÇ" value={b.cta_label?.[tab] || ""}
                        onChange={(e) => updateEditorialMl(idx, "cta_label", e.target.value)}/>
                    </div>
                    <div>
                      <label className="metadata text-[var(--text-muted)]">Düymə linki</label>
                      <input className="w-full bg-transparent border border-[var(--border)] px-3 py-2 mt-1 font-mono text-xs"
                        placeholder="/catalog" value={b.cta_url || ""}
                        onChange={(e) => updateEditorial(idx, "cta_url", e.target.value)}/>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="metadata text-[var(--text-muted)]">Sticker dəyəri</label>
                      <input className="w-full bg-transparent border border-[var(--border)] px-3 py-2 mt-1"
                        placeholder="məs: 180g, GC, 120+" value={b.stat_value || ""}
                        onChange={(e) => updateEditorial(idx, "stat_value", e.target.value)}/>
                    </div>
                    <div>
                      <label className="metadata text-[var(--text-muted)]">Sticker etiketi — {tab.toUpperCase()}</label>
                      <input className="w-full bg-transparent border border-[var(--border)] px-3 py-2 mt-1"
                        placeholder="premium pres" value={b.stat_label?.[tab] || ""}
                        onChange={(e) => updateEditorialMl(idx, "stat_label", e.target.value)}/>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="metadata text-[var(--text-muted)]">İkon</label>
                      <select className="w-full bg-transparent border border-[var(--border)] px-3 py-2 mt-1"
                        value={b.icon} onChange={(e) => updateEditorial(idx, "icon", e.target.value)}>
                        {EDITORIAL_ICONS.map((i) => <option key={i.v} value={i.v}>{i.l}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="metadata text-[var(--text-muted)]">Düzülüş</label>
                      <select className="w-full bg-transparent border border-[var(--border)] px-3 py-2 mt-1"
                        value={b.align} onChange={(e) => updateEditorial(idx, "align", e.target.value)}>
                        {EDITORIAL_ALIGNS.map((a) => <option key={a.v} value={a.v}>{a.l}</option>)}
                      </select>
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );

  const renderFeaturedArtist = () => (
    <div data-testid="featured-artist-editor">
      <SectionHeader icon={Disc3} title="Seçilmiş Sənətçi" desc="Ana səhifədəki böyük portretli sənətçi və “DİSKOQRAFIYA” sırası."/>
      <div className="space-y-6 max-w-3xl">
        <div>
          <label className="text-[11px] tracking-[0.04em] text-[var(--text-muted)] font-medium">Əsas sənətçi</label>
          <select className="w-full bg-transparent border border-[var(--border)] px-3 py-2 mt-1"
            value={data.featured_artist_id || ""}
            onChange={(e) => setData((d) => ({ ...d, featured_artist_id: e.target.value }))}
            data-testid="featured-artist-select">
            <option value="">— Avtomatik (siyahıdakı ilk sənətçi) —</option>
            {artistList.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
          </select>
        </div>
        <div>
          <label className="text-[11px] tracking-[0.04em] text-[var(--text-muted)] font-medium">Diskoqrafiya sırası (4-ə qədər)</label>
          <p className="text-xs text-[var(--text-muted)] mt-1">“DİSKOQRAFIYA” altında görsənəcək sənətçilər. Seçilməsə avtomatik digər sənətçilərdən doldurulur.</p>
          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-2 mt-3">
            {artistList.map((a) => {
              const selected = (data.discography_artist_ids || []).includes(a.id);
              const disabled = !selected && (data.discography_artist_ids || []).length >= 4;
              return (
                <button key={a.id} type="button" onClick={() => toggleDiscographyArtist(a.id)} disabled={disabled}
                  className={`text-left px-3 py-2 border text-sm transition-colors ${
                    selected ? "border-[var(--accent)] bg-[var(--accent)]/10 text-[var(--text)]"
                      : disabled ? "border-[var(--border-soft)] text-[var(--text-muted)] opacity-50 cursor-not-allowed"
                      : "border-[var(--border)] text-[var(--text-2)] hover:border-[var(--text)] hover:text-[var(--text)]"
                  }`} data-testid={`disco-toggle-${a.id}`}>
                  <span className="inline-block w-4 mr-1.5 align-middle">{selected ? "✓" : ""}</span>
                  <span className="align-middle">{a.name}</span>
                </button>
              );
            })}
            {artistList.length === 0 && (
              <div className="text-sm text-[var(--text-muted)] col-span-full">Hələ heç bir sənətçi əlavə edilməyib.</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  const renderFooter = () => (
    <div>
      <SectionHeader icon={FileText} title="Footer mətnləri" desc="Saytın altındakı haqqımızda, ünvan, copyright və şüar."/>
      <div className="space-y-5 max-w-2xl">
        {FIELDS[1].items.map((it) => (
          <div key={it.key}>
            <label className="text-[11px] tracking-[0.04em] text-[var(--text-muted)] font-medium">{it.label} — {tab.toUpperCase()}</label>
            {it.multiline ? (
              <textarea rows={3} className="w-full bg-transparent border border-[var(--border)] px-3 py-2 mt-1"
                value={data.footer?.[it.key]?.[tab] || ""}
                onChange={(e) => setData({ ...data, footer: { ...data.footer, [it.key]: { ...data.footer[it.key], [tab]: e.target.value } } })}
                data-testid={`f-footer-${it.key}`}/>
            ) : (
              <input className="w-full bg-transparent border border-[var(--border)] px-3 py-2 mt-1"
                value={data.footer?.[it.key]?.[tab] || ""}
                onChange={(e) => setData({ ...data, footer: { ...data.footer, [it.key]: { ...data.footer[it.key], [tab]: e.target.value } } })}
                data-testid={`f-footer-${it.key}`}/>
            )}
          </div>
        ))}
      </div>
    </div>
  );

  const renderBackupHero = () => (
    <div>
      <SectionHeader icon={Archive} title="Ehtiyat hero" desc="Banner heç olmadıqda göstərilən rezervə hero başlığı və şəkli."/>
      <div className="space-y-5 max-w-2xl">
        {HERO_FIELDS.map((it) => (
          <div key={it.key}>
            <label className="text-[11px] tracking-[0.04em] text-[var(--text-muted)] font-medium">{it.label} — {tab.toUpperCase()}</label>
            {it.multiline ? (
              <textarea rows={3} className="w-full bg-transparent border border-[var(--border)] px-3 py-2 mt-1"
                value={data.hero?.[it.key]?.[tab] || ""}
                onChange={(e) => setData({ ...data, hero: { ...data.hero, [it.key]: { ...data.hero[it.key], [tab]: e.target.value } } })}/>
            ) : (
              <input className="w-full bg-transparent border border-[var(--border)] px-3 py-2 mt-1"
                value={data.hero?.[it.key]?.[tab] || ""}
                onChange={(e) => setData({ ...data, hero: { ...data.hero, [it.key]: { ...data.hero[it.key], [tab]: e.target.value } } })}/>
            )}
          </div>
        ))}
        <div>
          <label className="text-[11px] tracking-[0.04em] text-[var(--text-muted)] font-medium">Ehtiyat hero şəkli</label>
          <div className="flex items-center gap-3 mt-1">
            {data.hero.image_url && <img src={data.hero.image_url} alt="" className="w-24 h-24 object-cover border border-[var(--border)]"/>}
            <label className="btn-outline cursor-pointer" style={{ textTransform: "none", letterSpacing: "0" }}>
              <Upload size={14}/> {uploadingHero ? "…" : "Yüklə"}
              <input type="file" accept="image/*" hidden onChange={onUploadHero}/>
            </label>
            <input className="flex-1 bg-transparent border border-[var(--border)] px-3 py-2 font-mono text-xs"
              value={data.hero.image_url}
              onChange={(e) => setData({ ...data, hero: { ...data.hero, image_url: e.target.value }})}/>
          </div>
        </div>
      </div>
    </div>
  );

  // ─── Main render ─────────────────────────────────────────────────────
  const renderActive = () => {
    if (activeSection === "brand") return renderBrand();
    if (activeSection === "social") return renderSocial();
    if (activeSection === "banners") return renderBanners();
    if (activeSection === "ambient") return renderAmbient();
    if (activeSection === "editorial") return renderEditorial();
    if (activeSection === "featured-artist") return renderFeaturedArtist();
    if (activeSection === "footer") return renderFooter();
    if (activeSection === "backup-hero") return renderBackupHero();
    if (activeSection.startsWith("page-")) return renderPageSection(activeSection.slice(5));
    return null;
  };

  return (
    <div className="flex min-h-screen" data-testid="admin-content">
      {/* ── Sidebar ── */}
      <aside className="w-72 lg:w-80 border-r border-[var(--border)] sticky top-0 self-start h-screen overflow-y-auto bg-[var(--surface)]/30 flex-shrink-0 hidden md:block">
        <div className="p-5 sticky top-0 bg-[var(--surface)]/95 backdrop-blur-md border-b border-[var(--border-soft)] z-10">
          <div className="text-[11px] tracking-[0.04em] text-[var(--text-muted)] font-medium">SİSTEM</div>
          <h1 className="font-display text-2xl mt-0.5">Sayt mətnləri</h1>
          <div className="mt-4 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" size={14}/>
            <input
              placeholder="Bölmə axtar..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full bg-transparent border border-[var(--border)] pl-9 pr-3 py-2 text-sm"
              data-testid="content-section-search"
            />
          </div>
        </div>
        <nav className="px-2 py-4">
          {groupedSections.length === 0 && (
            <div className="px-3 py-6 text-sm text-[var(--text-muted)]">Heç bir uyğun bölmə tapılmadı.</div>
          )}
          {groupedSections.map((group) => (
            <div key={group.name} className="mb-5">
              <div className="px-3 text-[10px] tracking-[0.16em] text-[var(--text-muted)] font-semibold mb-1 uppercase">{group.name}</div>
              {group.items.map((s) => {
                const Icon = s.icon;
                const isActive = activeSection === s.id;
                return (
                  <button
                    key={s.id}
                    onClick={() => setActiveSection(s.id)}
                    className={`w-full text-left px-3 py-2 flex items-center gap-2.5 text-sm transition-colors ${
                      isActive
                        ? "bg-[var(--text)] text-[var(--bg)]"
                        : "text-[var(--text-2)] hover:bg-[var(--surface)] hover:text-[var(--text)]"
                    }`}
                    data-testid={`section-${s.id}`}
                  >
                    <Icon size={14} className="flex-shrink-0"/>
                    <span className="flex-1 truncate">{s.label}</span>
                  </button>
                );
              })}
            </div>
          ))}
        </nav>
      </aside>

      {/* ── Content ── */}
      <main className="flex-1 min-w-0 flex flex-col">
        {/* Sticky header */}
        <div className="sticky top-0 z-20 bg-[var(--bg)]/95 backdrop-blur-md border-b border-[var(--border-soft)] px-5 md:px-10 py-4 flex items-center gap-4 flex-wrap">
          {/* Mobile section selector */}
          <select
            className="md:hidden bg-transparent border border-[var(--border)] px-3 py-2 text-sm w-full"
            value={activeSection}
            onChange={(e) => setActiveSection(e.target.value)}
            data-testid="mobile-section-select"
          >
            {SECTIONS.map((s) => (
              <option key={s.id} value={s.id}>{s.group} — {s.label}</option>
            ))}
          </select>

          <div className="hidden md:flex gap-1">
            {LANGS.map((l) => (
              <button
                key={l}
                onClick={() => setTab(l)}
                className={`px-4 py-1.5 text-xs font-medium border ${
                  tab === l
                    ? "bg-[var(--text)] text-[var(--bg)] border-[var(--text)]"
                    : "border-[var(--border)] text-[var(--text-2)] hover:border-[var(--text)] hover:text-[var(--text)]"
                }`}
                data-testid={`tab-${l}`}
              >
                {l.toUpperCase()}
              </button>
            ))}
          </div>

          <div className="md:hidden flex gap-1 w-full">
            {LANGS.map((l) => (
              <button
                key={l}
                onClick={() => setTab(l)}
                className={`flex-1 px-4 py-1.5 text-xs font-medium border ${
                  tab === l
                    ? "bg-[var(--text)] text-[var(--bg)] border-[var(--text)]"
                    : "border-[var(--border)] text-[var(--text-2)]"
                }`}
              >
                {l.toUpperCase()}
              </button>
            ))}
          </div>

          <div className="flex-1"/>

          {dirty && (
            <span className="hidden sm:inline-flex items-center gap-1.5 text-xs text-[var(--accent)] font-medium">
              <AlertCircle size={12}/> Yadda saxlanmamış dəyişikliklər
            </span>
          )}
          {msg && <span className="text-xs text-[var(--primary)]" data-testid="content-save-msg">{msg}</span>}
          <button
            onClick={save}
            disabled={busy || !dirty}
            className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ textTransform: "none", letterSpacing: "0" }}
            data-testid="content-save"
          >
            <Save size={14}/> {busy ? "…" : "Yadda saxla"}
          </button>
        </div>

        {/* Section body */}
        <div className="px-5 md:px-10 py-8 flex-1">
          {renderActive()}
        </div>
      </main>
    </div>
  );
}
